// Settings related to the user subscription
// --------------------------------------------------------
import api from '@/lib/api'
import {PlanCycle, Plans} from '@/lib/definitions'
import {orderBy} from 'lodash'

const state = {
  plan: {
    type: null,
    cancelled: false,
    activeUsers: null,
    uniqueUsers: null, // active + disabled users
    slots: null, // paid slots
    costPerUser: null,
    trialExpiresAt: null,
    legacy: false
  },
  planDetails: [], // {id, interval, label, costPerUser, percentDiscount}
  selectedPlanId: null, // planId
  promo: null,
  cards: [], // {_id/brand/last4/default...}
  billing: null // { address, zip, city, state, country, name, taxId }
}

const getters = {
  plan: state => state.plan,
  planDetails: state => state.planDetails,
  selectedPlanId: state => state.selectedPlanId,
  promo: state => state.promo,
  cards: state => state.cards,
  billing: state => state.billing,
  accountType: state => state.plan.type,
  costPerUser: state => state.plan.costPerUser || 0,
  legacy: state => state.plan.legacy,
  isFree: state => state.plan.type === Plans.FREE,
  isPremium: state => state.plan.type === Plans.PREMIUM,
  isPremiumDaily: state =>
    state.plan.type === Plans.PREMIUM && state.plan.label === PlanCycle.DAILY,
  isPremiumMonthly: state =>
    state.plan.type === Plans.PREMIUM && state.plan.label === PlanCycle.MONTHLY,
  isPremiumYearly: state =>
    state.plan.type === Plans.PREMIUM && state.plan.label === PlanCycle.YEARLY,
  isTrial: state => state.plan.type === Plans.TRIAL,
  isTrialExpired: (state, getters) => {
    const expiresDate = new Date(state.plan.trialExpiresAt)
    const isExpired = expiresDate - new Date() < 0
    return getters.isTrial && isExpired
  },
  isPlanNotValid: state => state.plan.type === 'noplan',
  trialDaysLeft: state => {
    const expiresDate = new Date(state.plan.trialExpiresAt)
    const leftTime = expiresDate - new Date()
    const leftTimeToDays = Math.ceil(leftTime / 1000 / 60 / 60 / 24)
    return leftTimeToDays > 0 ? leftTimeToDays : 0
  }
}

const mutations = {
  setSubscriptionData(state, data) {
    state.promo = data.promo
    if (state.promo) {
      state.promo.expiry = new Date(state.promo.expiry)
    }
    state.plan = data.plan
    state.planDetails = orderBy(data.planDetails, 'costPerUser')
    if (state.planDetails?.length > 0) {
      state.selectedPlanId = state.planDetails[0].id
    }
    state.cards = data.cards
    state.billing = data.billing
  },
  setPlan(
    state,
    {
      type = state.plan.type,
      cancelled = state.plan.cancelled,
      trialExpiresAt = state.plan.trialExpiresAt,
      activeUsers = state.plan.activeUsers,
      costPerUser = state.plan.costPerUser,
      legacy = state.plan.legacy
    }
  ) {
    state.plan = {
      type,
      cancelled,
      trialExpiresAt,
      activeUsers,
      costPerUser,
      legacy
    }
  },
  setCards(state, cards) {
    state.cards = cards
  },
  setBilling(state, billing) {
    state.billing = billing
  },
  setPlanId(state, planId) {
    state.selectedPlanId = planId
  },
  setAutoRenew(state, b) {
    state.plan.autoRenew = b
  }
}

const actions = {
  setPlan({commit}, payload) {
    commit('setPlan', payload)
  },
  forceTrialSubscription(context) {
    return context.dispatch('expireTrialSubscription').then(() => {
      return context.dispatch('setTrialSubscription')
    })
  },
  expireTrialSubscription({commit, dispatch}) {
    return api.call('post', '/subscriptions/trial/expire').then(res => {
      commit('setPlan', res.plan)
      dispatch('setLandscapes', res.user)
    })
  },
  setTrialSubscription({commit, dispatch}) {
    return api.call('post', '/subscriptions/trial/').then(res => {
      commit('setPlan', res.plan)
      dispatch('setLandscapes', res.user)
    })
  },
  getSubscriptionInfo(context) {
    return api.call('get', '/subscriptions/').then(res => {
      context.commit('setSubscriptionData', res)
    })
  },
  getAllSubscriptionLandscapes() {
    return api.call('get', '/subscriptions/landscapes').then(landscapes => {
      return landscapes.map(landscape => {
        const users = Object.keys(landscape.users).map(
          id => landscape.users[id]
        )
        return Object.assign(landscape, {users})
      })
    })
  },
  setFreeSubscription({commit, dispatch}, payload) {
    return api.call('post', '/subscriptions/free', payload).then(res => {
      commit('setPlan', res.plan)
      dispatch('setLandscapes', res.user)
    })
  },
  checkout(context, payload) {
    return api.call('post', '/subscriptions/checkout', payload)
  },
  portal(context, payload) {
    return api.call('post', '/subscriptions/portal', payload)
  },
  toggleAutoRenew({commit}, payload) {
    return api
      .call('post', '/subscriptions/renewal', {renew: payload})
      .then(() => {
        commit('setAutoRenew', payload)
        return payload
      })
  },
  addBillingAddress({commit}, payload) {
    return api
      .call('put', '/subscriptions/billing', payload)
      .then(res => commit('setBilling', res.billing))
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
